/**
 * AR launcher for the configurator — "View in your space".
 *
 * ── Why this uses NATIVE links, not model-viewer ──────────────────────────────
 * The configurator already runs a full three.js WebGL context. Spinning up a
 * model-viewer (a SECOND WebGL context + the model loaded again + in-browser USDZ
 * conversion) on top of it crashes the Safari tab on iPhone ("a problem occurred
 * repeatedly"). The native AR entry points need no WebGL at all:
 *   - iOS Safari  → an <a rel="ar" href="model.usdz"><img></a> the browser
 *     intercepts to open Quick Look. (Quick Look needs a real .usdz, not a GLB.)
 *   - Android Chrome → navigate to a Scene Viewer intent:// URL pointing at the GLB.
 * Both require an absolute https URL (works on the Netlify deploy) and a real
 * user gesture (we trigger synchronously from the tap). Desktop → QR.
 *
 * Config (ViewerOptions): `arModel` = GLB (Android), `arModelIos` = USDZ (iOS).
 */

import { showToast } from "./Toast";

function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports as Mac; detect touch Macs.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}
function isMobileLike(): boolean {
  return isIOS() || isAndroid() || matchMedia("(max-width: 768px)").matches;
}
function supportsRelAr(): boolean {
  const a = document.createElement("a");
  return a.relList && a.relList.supports && a.relList.supports("ar");
}
function absUrl(u: string): string {
  try { return new URL(u, location.href).toString(); }
  catch { return u; }
}

/* ── iOS Quick Look: rel="ar" anchor with a first-child <img> (required) ───── */

function launchIosQuickLook(usdzUrl: string): boolean {
  // Counter BEFORE the capability guard: headless WebKit (QA sweep) has no Quick
  // Look, but the attempt itself is what the single-tap contract asserts.
  bumpArLaunchCounter();
  if (!supportsRelAr()) return false; // not Safari / no Quick Look → caller falls back
  const a = document.createElement("a");
  a.setAttribute("rel", "ar");
  a.href = absUrl(usdzUrl);
  // Safari REQUIRES a single <img> first child or it won't open Quick Look.
  const img = document.createElement("img");
  img.style.display = "none";
  a.appendChild(img);
  document.body.appendChild(a);
  a.click();
  setTimeout(() => a.remove(), 1000);
  return true;
}

/* ── Android Scene Viewer: intent:// to the GLB ────────────────────────────── */

/** Probe instrumentation: counts actual AR launch attempts (anchor click /
 *  intent navigation) so the QA sweep can assert the single-tap contract. */
function bumpArLaunchCounter(): void {
  const w = window as unknown as { __NEOVAA_AR_LAUNCHES__?: number };
  w.__NEOVAA_AR_LAUNCHES__ = (w.__NEOVAA_AR_LAUNCHES__ ?? 0) + 1;
}

function launchAndroidSceneViewer(glbUrl: string): void {
  bumpArLaunchCounter();
  const file = encodeURIComponent(absUrl(glbUrl));
  const fallback = encodeURIComponent(location.href);
  const intent =
    `intent://arvr.google.com/scene-viewer/1.0?file=${file}&mode=ar_preferred` +
    `#Intent;scheme=https;package=com.google.android.googlequicksearchbox;` +
    `action=android.intent.action.VIEW;S.browser_fallback_url=${fallback};end;`;
  // Synchronous navigation from the tap → Scene Viewer (separate app) fetches the GLB.
  window.location.href = intent;
}

/* ── Desktop QR fallback ─────────────────────────────────────────────────── */

let qrcodeLoad: Promise<void> | null = null;
function loadQrcode(): Promise<void> {
  if (qrcodeLoad) return qrcodeLoad;
  qrcodeLoad = new Promise<void>((resolve, reject) => {
    if ((window as unknown as { QRCode?: unknown }).QRCode) return resolve();
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js";
    s.addEventListener("load", () => resolve());
    s.addEventListener("error", () => reject(new Error("QR library failed to load")));
    document.head.appendChild(s);
  });
  return qrcodeLoad;
}

function buildScanUrl(): string {
  const base = location.href;
  try {
    const u = new URL(base);
    u.searchParams.set("neovaa_ar", "auto");
    return u.toString();
  } catch {
    return base + (base.includes("?") ? "&" : "?") + "neovaa_ar=auto";
  }
}

async function openQrModal(): Promise<void> {
  try {
    await loadQrcode();
  } catch {
    showToast("Open this page on your phone to view in your space");
    return;
  }
  let modal = document.getElementById("nv-qr-modal");
  if (!modal) {
    modal = document.createElement("div");
    modal.id = "nv-qr-modal";
    modal.className = "nv-qr-modal";
    modal.innerHTML = `
      <div class="nv-qr-card" role="dialog" aria-label="View in AR">
        <button class="nv-qr-close" aria-label="Close">&times;</button>
        <div class="nv-qr-title">Open this in AR</div>
        <div class="nv-qr-sub">Scan with your phone camera</div>
        <div class="nv-qr-box" id="nv-qr-box"></div>
      </div>`;
    document.body.appendChild(modal);
    const close = () => modal!.classList.remove("open");
    modal.querySelector(".nv-qr-close")?.addEventListener("click", close);
    modal.addEventListener("click", (e) => { if (e.target === modal) close(); });
    window.addEventListener("keydown", (e) => { if (e.key === "Escape") close(); });
  }
  const box = modal.querySelector<HTMLElement>("#nv-qr-box");
  if (box) {
    box.innerHTML = "";
    const QR = (window as unknown as { QRCode: new (el: HTMLElement, opts: object) => void }).QRCode;
    new QR(box, { text: buildScanUrl(), width: 200, height: 200 });
  }
  modal.classList.add("open");
}

/* ── Public API ───────────────────────────────────────────────────────────── */

export interface ArSources {
  /** GLB for Android Scene Viewer (viewer.arModel). */
  glb?: string;
  /** USDZ for iOS Quick Look (viewer.arModelIos). */
  usdz?: string;
}

/** Positively-confirmed WiFi/ethernet only. Returns false when unknown — which is
 *  ALWAYS the case on iOS Safari (no Network Information API), so the heavy iOS
 *  USDZ never pre-downloads on cellular OR when the network is unknowable. The
 *  data-conscious default (Alex 2026-05-31: "prefetch only on WiFi"). */
function isConfirmedWifi(): boolean {
  const c = (navigator as unknown as {
    connection?: { type?: string; saveData?: boolean };
  }).connection;
  if (!c || c.saveData) return false;
  return c.type === "wifi" || c.type === "ethernet";
}

/** Prefetch the AR asset so the native viewer opens fast — but ONLY on confirmed
 *  WiFi (see isConfirmedWifi). On iPhone (no connection API) this never fires, so
 *  the ~27MB USDZ downloads at tap time instead → zero wasted cellular data. Just
 *  a <link rel="prefetch">; no WebGL, never blocks navigation. */
export function prepareAR(sources: ArSources): void {
  if (!isMobileLike()) return;
  if (!isConfirmedWifi()) return; // unknown/cellular → wait for the AR tap
  const url = isIOS() ? sources.usdz : sources.glb;
  if (!url) return;
  try {
    const l = document.createElement("link");
    l.rel = "prefetch";
    l.href = absUrl(url);
    document.head.appendChild(l);
  } catch { /* best-effort */ }
}

/** SYNCHRONOUS tap handler. iOS → Quick Look (USDZ). Android → Scene Viewer (GLB).
 *  Desktop → QR. Honest fallback messages; never crashes the page. */
export function launchAR(sources: ArSources): () => void {
  return () => {
    if (isIOS()) {
      if (!sources.usdz) { showToast("AR not available for this product"); return; }
      const ok = launchIosQuickLook(sources.usdz);
      if (!ok) showToast("Pour la réalité augmentée, ouvre cette page dans Safari");
      return;
    }
    if (isAndroid()) {
      if (!sources.glb) { showToast("AR not available for this product"); return; }
      launchAndroidSceneViewer(sources.glb);
      return;
    }
    // Desktop → QR so they continue on a phone.
    void openQrModal();
  };
}

/** Arrived via the desktop QR (?neovaa_ar=auto): fire AR on arrival. */
export function maybeAutoLaunchAR(sources: ArSources): void {
  try {
    const p = new URLSearchParams(location.search);
    if (p.get("neovaa_ar") !== "auto") return;
    if (!isMobileLike()) return;
    p.delete("neovaa_ar");
    const clean = location.pathname + (p.toString() ? `?${p}` : "") + location.hash;
    history.replaceState(null, "", clean);
    // Auto-fire isn't a user gesture; iOS may need a tap. Best-effort.
    launchAR(sources)();
  } catch {
    /* never block boot */
  }
}
